import { Link } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import SectionHeading from "@/components/feature/SectionHeading";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const team = [
  { name: "内容策划组", role: "影视资讯与专题策划", desc: "负责影视新闻、影评与专题内容的策划与编辑。", icon: "ri-edit-2-line" },
  { name: "数据与推荐组", role: "分类体系与推荐逻辑", desc: "负责影片资料的整理、分类标签与推荐内容的维护。", icon: "ri-bar-chart-box-line" },
  { name: "产品设计组", role: "网站体验与视觉呈现", desc: "负责网站的界面设计、交互体验与移动端适配优化。", icon: "ri-layout-masonry-line" },
  { name: "技术运营组", role: "站点稳定与内容更新", desc: "负责网站运行维护、内容更新与用户体验保障。", icon: "ri-server-line" },
];

export default function About() {
  useDocumentMeta({
    title: "关于我们 - 八戒影视",
    description:
      "了解八戒影视：一个面向影视爱好者的内容平台，致力于提供最新电影电视剧资讯推荐与丰富的影视资源介绍。",
    keywords: "八戒影视,关于我们,影视平台,影视资讯,影视推荐",
  });

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />

      {/* 网站定位 */}
      <section className="rounded-xl bg-secondary-950 px-6 py-10 md:px-12 md:py-14">
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-secondary-800 text-accent-400 text-xs mb-4">
          <i className="ri-film-line"></i>
          关于八戒影视
        </span>
        <h1 className="text-2xl md:text-4xl font-bold text-background-50 leading-tight max-w-3xl">
          发现值得一看的影视作品，从了解资讯开始
        </h1>
        <p className="text-sm md:text-base text-background-200/80 mt-4 max-w-2xl leading-relaxed">
          八戒影视是一个面向影视爱好者的内容平台，聚焦影视资讯、影视推荐与影片详细介绍。
          我们希望通过及时的资讯更新、清晰的内容分类与优质的作品介绍，帮助用户更轻松地发现影视佳作、了解作品背景，
          并找到符合自己口味的观影选择。无论是追剧爱好者，还是关注影视圈动态的观众，都能在这里获得有价值的内容。
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mt-8">
          {[
            { num: "18+", label: "收录影视作品" },
            { num: "3", label: "核心影视分类" },
            { num: "9+", label: "资讯与影评文章" },
            { num: "每日", label: "内容持续更新" },
          ].map((s) => (
            <div key={s.label}>
              <p className="text-2xl md:text-3xl font-bold text-accent-400">{s.num}</p>
              <p className="text-xs md:text-sm text-background-200/70 mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 我们做什么 */}
      <section className="mt-12">
        <SectionHeading title="我们能为你做什么" subtitle="围绕影视资讯与影片资源分享的核心能力" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          {[
            { icon: "ri-newspaper-line", title: "新鲜资讯", desc: "及时同步影视行业动态、上映信息与热门话题，让你不错过任何一条重要新闻。" },
            { icon: "ri-movie-2-line", title: "影视推荐", desc: "按电影、电视剧、综艺等类型整理作品，结合评分与看点，帮你快速锁定想看的内容。" },
            { icon: "ri-file-text-line", title: "详细介绍", desc: "每部作品都提供主创阵容、类型标签、剧情简介与相关推荐，内容完整、阅读舒适。" },
          ].map((f) => (
            <div key={f.title} className="p-6 rounded-lg bg-background-100 border border-background-200">
              <span className="w-12 h-12 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center mb-4">
                <i className={`${f.icon} text-2xl`}></i>
              </span>
              <h3 className="text-base font-bold text-foreground-950">{f.title}</h3>
              <p className="text-sm text-foreground-600 mt-2 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 团队介绍 */}
      <section className="mt-12">
        <SectionHeading title="团队介绍" subtitle="一个热爱影视、专注内容的小团队" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {team.map((t) => (
            <div key={t.name} className="p-5 rounded-lg bg-background-100 border border-background-200">
              <span className="w-11 h-11 rounded-full bg-secondary-100 text-secondary-700 flex items-center justify-center mb-4">
                <i className={`${t.icon} text-xl`}></i>
              </span>
              <h3 className="text-sm font-bold text-foreground-950">{t.name}</h3>
              <p className="text-xs text-accent-600 mt-1">{t.role}</p>
              <p className="text-xs text-foreground-500 mt-3 leading-relaxed">{t.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 联系方式 + 使用须知 */}
      <section className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
        <div className="p-6 md:p-8 rounded-lg bg-background-100 border border-background-200">
          <h2 className="text-lg font-bold text-foreground-950 mb-4">联系我们</h2>
          <p className="text-xs text-foreground-400 mb-4">以下联系方式为演示信息，非真实账号。</p>
          <ul className="space-y-3 text-sm">
            <li className="flex items-center gap-3 text-foreground-700">
              <span className="w-9 h-9 rounded-md bg-background-200 flex items-center justify-center text-foreground-500">
                <i className="ri-mail-line"></i>
              </span>
              contact@bajie-demo.com
            </li>
            <li className="flex items-center gap-3 text-foreground-700">
              <span className="w-9 h-9 rounded-md bg-background-200 flex items-center justify-center text-foreground-500">
                <i className="ri-phone-line"></i>
              </span>
              400-000-0000（演示）
            </li>
            <li className="flex items-center gap-3 text-foreground-700">
              <span className="w-9 h-9 rounded-md bg-background-200 flex items-center justify-center text-foreground-500">
                <i className="ri-map-pin-line"></i>
              </span>
              北京市朝阳区影视产业园 A 座（演示）
            </li>
          </ul>
          <div className="flex items-center gap-3 mt-6">
            {["ri-wechat-line", "ri-weibo-line", "ri-youtube-line"].map((icon) => (
              <span
                key={icon}
                className="w-10 h-10 rounded-full bg-background-200 flex items-center justify-center text-foreground-600 hover:bg-primary-500 hover:text-background-50 transition-colors cursor-pointer"
              >
                <i className={icon}></i>
              </span>
            ))}
          </div>
        </div>

        <div className="p-6 md:p-8 rounded-lg bg-background-100 border border-background-200">
          <h2 className="text-lg font-bold text-foreground-950 mb-4">使用须知</h2>
          <ul className="space-y-3 text-sm text-foreground-600">
            {[
              "本站所有影视资讯、影片信息与图片均为演示数据，仅用于产品功能展示。",
              "站内不提供任何影片的在线播放或下载服务，也不存储任何影视资源文件。",
              "页面中的收藏、分享、评分等功能为前端演示，不代表真实服务，也不会产生实际数据。",
              "如有任何内容涉及版权问题，请通过演示联系方式与我们沟通，我们会及时处理。",
            ].map((text, i) => (
              <li key={i} className="flex gap-2.5">
                <i className="ri-checkbox-circle-line text-primary-500 mt-0.5"></i>
                <span>{text}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="mt-12">
        <div className="rounded-lg border border-primary-200 bg-primary-50 p-6 md:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-bold text-foreground-950">准备好开始探索了吗？</h2>
            <p className="text-sm text-foreground-600 mt-1">浏览最新影视资讯，或直接查看热门影视推荐。</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/news/行业新闻"
              className="px-5 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
            >
              浏览影视资讯
            </Link>
            <Link
              to="/category/电影"
              className="px-5 py-2.5 rounded-md border border-primary-300 text-primary-700 hover:bg-primary-100 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
            >
              查看电影推荐
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}